import type { FC } from "react";
import { useEffect, useState, useMemo, useRef } from "react";
import { TableTop } from "app/pages/industry-analysis/market-builder/tabs/industry/table/table-top/TableTop";
import { MAX_DOMAINS_IN_CATEGORY } from "app/components/customCategoriesWizard/custom-categories-max-domains";
import { SWReactTableWithSelection } from "app/components/React/Table/SWReactTableWrapperWithSelection";
import type { IDomains } from "app/pages/industry-analysis/market-builder/MarketBuilder.types";
import type { ICountryObject } from "app/services/CountryService";
import type { ICategory } from "app/common/services/categoryService.types";
import { getColumns } from "app/pages/industry-analysis/market-builder/tabs/industry/table/columns";
import { INITIAL_RAW_DATA_STATE } from "app/pages/industry-analysis/market-builder/tabs/industry/table/constants";
import { fetchData } from "app/pages/industry-analysis/market-builder/tabs/industry/table/fetchData";
import type { IIndustryState } from "app/pages/industry-analysis/market-builder/tabs/types";
import { DomainSelectionWrapper } from "app/pages/industry-analysis/market-builder/tabs/common/domain-selection/DomainSelectionWrapper";
import { useDidMountEffect } from "app/custom-hooks/useDidMountEffect";
import { getTableData } from "app/pages/industry-analysis/market-builder/tabs/common/getTableData";
import { TableLoader } from "app/pages/industry-analysis/market-builder/tabs/common/TableLoader";
import { allTrackers } from "app/services/track/track";
import { MAX_DOMAINS_ALLOWED } from "app/pages/industry-analysis/market-builder/services/ImportDomainsHandler";

interface ITable {
    selectedDomains: IDomains;
    addDomains: (domains: IDomains) => void;
    selectedCountry: ICountryObject;
    selectedIndustry: ICategory;
    industryTabState: IIndustryState;
    setIndustryTabState: (industryTabState: IIndustryState) => void;
}
const TABLE_SELECTION_KEY = "MARKET_BUILDER_BY_INDUSTRY";
const TABLE_SELECTION_PROPERTY = "Domain";

export const Table: FC<ITable> = (props) => {
    const {
        selectedDomains = [],
        selectedCountry,
        selectedIndustry,
        addDomains,
        setIndustryTabState,
        industryTabState,
    } = props;
    const { tableRawData: rawData, filters } = industryTabState;
    const setStateHof = (attribute: keyof IIndustryState) => (value) => {
        setIndustryTabState({ ...industryTabState, [attribute]: value });
    };
    const setRawData = setStateHof("tableRawData");
    const setFilters = setStateHof("filters");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const clearAllSelectedRowsRef = useRef<VoidFunction>(null);
    const { orderBy, traffic, websiteType, page } = filters;
    const { isUniqueToggle, uniqueUsers, monthlyVisits } = traffic;
    const { sortDirection, field } = orderBy;
    const IS_ADD_DISABLED = selectedDomains.length >= MAX_DOMAINS_ALLOWED;
    const tableData = useMemo(() => {
        const tableData = getTableData(rawData, selectedDomains, selectedCountry);
        return tableData;
    }, [rawData, selectedDomains]);
    const loadData = () => {
        if (clearAllSelectedRowsRef.current) {
            clearAllSelectedRowsRef.current();
        }
        setIsLoading(true);
        fetchData(selectedCountry, selectedIndustry, filters)
            .then(setRawData)
            .catch(() => setRawData(INITIAL_RAW_DATA_STATE))
            .finally(() => setIsLoading(false));
    };
    useDidMountEffect(loadData, [
        sortDirection,
        field,
        websiteType.id,
        selectedIndustry.id,
        selectedCountry.id,
        isUniqueToggle,
        uniqueUsers.from,
        uniqueUsers.to,
        monthlyVisits.from,
        monthlyVisits.to,
        page,
    ]);
    useEffect(() => {
        if (rawData.Data.length !== 0) {
            return;
        }
        loadData();
    }, []);
    const onSort = ({ field, sortDirection }) => {
        setFilters({ ...filters, orderBy: { field, sortDirection } });
    };

    const maxSelectedRows = MAX_DOMAINS_IN_CATEGORY - selectedDomains.length;

    const onAddDomains = (domains: IDomains) => {
        addDomains(domains);

        const { traffic, websiteType } = filters;
        const EVENT_CATEGORY = "Addtolistfilters";

        allTrackers.trackEvent(
            EVENT_CATEGORY,
            "monthlyVisits",
            `By industry/${traffic.monthlyVisits.from}/${traffic.monthlyVisits.to}`,
        );

        allTrackers.trackEvent(
            EVENT_CATEGORY,
            "uniqueVisitors",
            `By industry/${traffic.uniqueUsers.from}/${traffic.uniqueUsers.to}`,
        );

        allTrackers.trackEvent(
            EVENT_CATEGORY,
            "Filters",
            `By industry/${websiteType.text}/${traffic.isUniqueToggle ? "On" : "Off"}`,
        );
    };

    return (
        <>
            <TableTop
                filters={filters}
                setFilters={setFilters}
            />
            {isLoading ? (
                <TableLoader />
            ) : (
                <SWReactTableWithSelection
                        tableSelectionKey={TABLE_SELECTION_KEY}
                        tableData={tableData}
                        tableSelectionProperty={TABLE_SELECTION_PROPERTY}
                        maxSelectedRows={maxSelectedRows}
                        cleanOnUnMount={false}
                        deleteTableSelectionOnUnMount={false}
                        tableColumns={getColumns(orderBy)}
                        tableOptions={{
                            tableSelectionTrackingParam: "Domain",
                            trackName: "Traffic Sources",
                            aboveHeaderComponents: [
                                <DomainSelectionWrapper
                                    key={TABLE_SELECTION_KEY}
                                    addDomains={onAddDomains}
                                    clearAllSelectedRowsRef={clearAllSelectedRowsRef}
                                    isDisabled={IS_ADD_DISABLED}
                                />,
                            ],
                        }}
                        onSort={onSort}
                    ></SWReactTableWithSelection>
            )}
        </>
    );
};
